export enum DropOffLocation {
  AS_INSTRUCTED = 'AS_INSTRUCTED',
  ATTENDED_DELIVERY = 'ATTENDED_DELIVERY',
  BEHIND_GARBAGE_BIN = 'BEHIND_GARBAGE_BIN',
  BICYCLE_BASKET = 'BICYCLE_BASKET',
  DOORSTEP = 'DOORSTEP',
  FRONT_DOOR = 'FRONT_DOOR',
  GARAGE = 'GARAGE',
  GARDEN = 'GARDEN',
  GAS_METER = 'GAS_METER',
  GREENHOUSE = 'GREENHOUSE',
  INSIDE_BOX = 'INSIDE_BOX',
  INSIDE_CAR = 'INSIDE_CAR',
  INSIDE_HOME = 'INSIDE_HOME',
  LOCKER = 'LOCKER',
  MAIL_ROOM_CLERK = 'MAIL_ROOM_CLERK',
  MAIL_ROOM_OR_SLOT = 'MAIL_ROOM_OR_SLOT',
  NEIGHBOR = 'NEIGHBOR',
  PICKUP_POINT = 'PICKUP_POINT',
  PORCH = 'PORCH',
  PROPERTY_OFFICE = 'PROPERTY_OFFICE',
  REAR_DOOR = 'REAR_DOOR',
  RECEPTIONIST = 'RECEPTIONIST',
  SECURITY = 'SECURITY',
  SHED = 'SHED',
  SIDE_PORCH = 'SIDE_PORCH',
  STORE = 'STORE',
  TERRACE = 'TERRACE',
  UNKNOWN = 'UNKNOWN'
}

const PACKAGE_DROPOFF_LOCATION_PREFIX = 'swiship_dropoff_location_';

const dropOffLocationMap: Map<string, DropOffLocation> = new Map([
  ['AS_INSTRUCTED', DropOffLocation.AS_INSTRUCTED],
  ['ATTENDED_DELIVERY', DropOffLocation.ATTENDED_DELIVERY],
  ['BEHIND_GARBAGE_BIN', DropOffLocation.BEHIND_GARBAGE_BIN],
  ['BICYCLE_BASKET', DropOffLocation.BICYCLE_BASKET],
  ['DOORSTEP', DropOffLocation.DOORSTEP],
  ['FRONT_DOOR', DropOffLocation.FRONT_DOOR],
  ['GARAGE', DropOffLocation.GARAGE],
  ['GARDEN', DropOffLocation.GARDEN],
  ['GAS_METER', DropOffLocation.GAS_METER],
  ['GREENHOUSE', DropOffLocation.GREENHOUSE],
  ['INSIDE_BOX', DropOffLocation.INSIDE_BOX],
  ['INSIDE_CAR', DropOffLocation.INSIDE_CAR],
  ['INSIDE_HOME', DropOffLocation.INSIDE_HOME],
  ['LOCKER', DropOffLocation.LOCKER],
  ['MAIL_ROOM_CLERK', DropOffLocation.MAIL_ROOM_CLERK],
  ['MAIL_ROOM_OR_SLOT', DropOffLocation.MAIL_ROOM_OR_SLOT],
  ['NEIGHBOR', DropOffLocation.NEIGHBOR],
  ['PICKUP_POINT', DropOffLocation.PICKUP_POINT],
  ['PORCH', DropOffLocation.PORCH],
  ['PROPERTY_OFFICE', DropOffLocation.PROPERTY_OFFICE],
  ['REAR_DOOR', DropOffLocation.REAR_DOOR],
  ['RECEPTIONIST', DropOffLocation.RECEPTIONIST],
  ['SECURITY', DropOffLocation.SECURITY],
  ['SHED', DropOffLocation.SHED],
  ['SIDE_PORCH', DropOffLocation.SIDE_PORCH],
  ['STORE', DropOffLocation.STORE],
  ['TERRACE', DropOffLocation.TERRACE],
  ['UNKNOWN', DropOffLocation.UNKNOWN]
]);

export const getDropOffLocation = (locationString: string): DropOffLocation => {
  return dropOffLocationMap.get(locationString) || DropOffLocation.UNKNOWN;
};

export const toSwishipString = (location: DropOffLocation): string => {
  return PACKAGE_DROPOFF_LOCATION_PREFIX + location.toLowerCase();
};

const RECIPIENT_INFO_LOCATIONS = [
  DropOffLocation.NEIGHBOR,
  DropOffLocation.RECEPTIONIST,
  DropOffLocation.MAIL_ROOM_CLERK,
  DropOffLocation.ATTENDED_DELIVERY,
  DropOffLocation.AS_INSTRUCTED
];

export const shouldShowRecipientInformation = (packageDropOffLocation?: string, hasPOD?: boolean): boolean => {
  if (!packageDropOffLocation) return false;
  
  // When both POD and recipient info are available, only NEIGHBOR shows recipient info
  if (hasPOD) {
    return packageDropOffLocation === toSwishipString(DropOffLocation.NEIGHBOR);
  }
  
  // When only recipient info is available, check all allowed locations
  return RECIPIENT_INFO_LOCATIONS.some(location => packageDropOffLocation === toSwishipString(location));
};

const RECIPIENT_MESSAGE_MAP: Partial<Record<DropOffLocation, {
  hasName?: string;
  default?: string;
  hasNameAndDoor?: string;
  hasDoor?: string;
}>> = {
  [DropOffLocation.RECEPTIONIST]: {
    hasNameAndDoor: 'swiship_delivered_to_receptionist_with_name_and_door',
    hasName: 'swiship_delivered_to_receptionist_with_name',
    hasDoor: 'swiship_delivered_to_receptionist_with_door',
    default: 'swiship_delivered_to_receptionist'
  },
  [DropOffLocation.NEIGHBOR]: {
    hasNameAndDoor: 'swiship_delivered_to_neighbor_with_name_and_door',
    hasName: 'swiship_delivered_to_neighbor_with_name',
    hasDoor: 'swiship_delivered_to_neighbor_with_door',
    default: 'swiship_delivered_to_neighbor'
  },
  [DropOffLocation.MAIL_ROOM_CLERK]: {
    hasNameAndDoor: 'swiship_delivered_to_mail_room_clerk_with_name_and_door',
    hasName: 'swiship_delivered_to_mail_room_clerk_with_name',
    hasDoor: 'swiship_delivered_to_mail_room_clerk_with_door',
    default: 'swiship_delivered_to_mail_room_clerk'
  },
  [DropOffLocation.ATTENDED_DELIVERY]: {
    hasNameAndDoor: 'swiship_delivered_to_attended_delivery_with_name_and_door',
    hasName: 'swiship_delivered_to_attended_delivery_with_name',
    hasDoor: 'swiship_delivered_to_attended_delivery_with_door',
    default: 'swiship_delivered_to_attended_delivery'
  },
  [DropOffLocation.AS_INSTRUCTED]: {
    hasNameAndDoor: 'swiship_delivered_to_as_instructed_with_name_and_door',
    hasName: 'swiship_delivered_to_as_instructed_with_name',
    hasDoor: 'swiship_delivered_to_as_instructed_with_door',
    default: 'swiship_delivered_to_as_instructed'
  }
};

export const getRecipientMessage = (
  packageDropOffLocation: string,
  recipientName?: string | null,
  recipientDoorNumber?: string | null
): string | null => {
  const locationKey = packageDropOffLocation.startsWith(PACKAGE_DROPOFF_LOCATION_PREFIX) 
    ? packageDropOffLocation.replace(PACKAGE_DROPOFF_LOCATION_PREFIX, '').toUpperCase()
    : packageDropOffLocation;
    
  const dropOffLocation = getDropOffLocation(locationKey);
  const messageConfig = RECIPIENT_MESSAGE_MAP[dropOffLocation];
  if (!messageConfig) return null;

  if (recipientName && recipientDoorNumber && messageConfig.hasNameAndDoor) return messageConfig.hasNameAndDoor;
  if (recipientName && messageConfig.hasName) return messageConfig.hasName;
  if (recipientDoorNumber && messageConfig.hasDoor) return messageConfig.hasDoor;
  return messageConfig.default ?? null;
};
